/**
 * Filing receipts in the remote store once they are booked (or rejected).
 *
 * Booked:   Inkorg/x.jpg → Kvitton/<år>/A12 2026-03-04 Clas Ohlson 625,00.jpg
 *           with metadata {companyId, verifikationId, series, number}
 * Rejected: Inkorg/x.jpg → Ej bokfört/<år>/x.jpg (private / skipped; never deleted)
 *
 * Failures surface as `RemoteStoreError`; the archive check ("Kontrollera
 * arkivet") re-uploads anything that never made it from the local blob.
 *
 * `driveFileId` keeps its name because that is the field on `attachments` and
 * `inboxItems`; renaming those is a Dexie migration, not part of the port.
 *
 * A store whose ids are paths (`capabilities.stableIdsAcrossMove: false`)
 * gives a filed receipt a *new* id, so the stored reference is rewritten right
 * after the move. Without that the archive check would report every booked
 * receipt as missing and the hydration could never fetch its bytes back.
 */
import { RemoteStoreError, getRemoteStore, type RemoteEntry, type RemoteId } from '@/cloud/store';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { formatSek, type Ore } from '@/domain/money';
import type { Uuid, Verifikation } from '@/domain/types';
import { ensureCompanyTree, ensureYearFolder, yearOf } from './tree';

const MAX_COUNTERPARTY_CHARS = 60;

/** Strip characters Drive or humans dislike in a file name. */
export function sanitizeFileNamePart(input: string): string {
  return input
    .replace(/[\x00-\x1f\x7f]/g, '')
    .replace(/[/\\:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** "625,00" / "14 380,00" / "-99,50" – öre formatted for a file name (regular spaces). */
export function amountForFileName(amountOre: Ore): string {
  return formatSek(amountOre).replace(/[  ]/g, ' ').replace(/−/g, '-');
}

/** File extension (with dot) taken from the current remote name, else from the mime type. */
export function extensionFor(name: string | undefined, mimeType: string | undefined): string {
  const fromName = name ? /\.([A-Za-z0-9]{1,5})$/.exec(name)?.[1] : undefined;
  if (fromName) return `.${fromName.toLowerCase()}`;
  switch (mimeType) {
    case 'image/jpeg':
      return '.jpg';
    case 'image/png':
      return '.png';
    case 'image/webp':
      return '.webp';
    case 'application/pdf':
      return '.pdf';
    default:
      return '';
  }
}

export interface ReceiptNameInput {
  series: string;
  number: number;
  date: string;
  counterparty: string;
  amountOre: Ore;
  /** With leading dot, e.g. ".jpg". */
  extension: string;
  /** 1-based index when one verifikation has several files. */
  index?: number;
}

/** `A12 2026-03-04 Clas Ohlson 625,00.jpg` (`A12 … (2).jpg` for a second file). */
export function receiptFileName(i: ReceiptNameInput): string {
  const counterparty = sanitizeFileNamePart(i.counterparty).slice(0, MAX_COUNTERPARTY_CHARS).trim() || 'Kvitto';
  const suffix = i.index && i.index > 1 ? ` (${i.index})` : '';
  return `${i.series}${i.number} ${i.date} ${counterparty} ${amountForFileName(i.amountOre)}${suffix}${i.extension}`;
}

/** The metadata written on a filed receipt; queryable by the archive check. */
export function receiptAppProperties(companyId: Uuid, v: Pick<Verifikation, 'id' | 'series' | 'number'>): Record<string, string> {
  return { companyId, verifikationId: v.id, series: v.series, number: String(v.number) };
}

export interface FileReceiptInput {
  companyId: Uuid;
  driveFileId: string;
  verifikation: Verifikation;
  counterparty: string;
  amountOre: Ore;
  index?: number;
}

async function companyOrThrow(companyId: Uuid, db: BookkeepingDB) {
  const company = await db.companies.get(companyId);
  if (!company) throw new Error(`Company ${companyId} not found`);
  return company;
}

/**
 * Point the local rows at the file's new id after a move.
 *
 * A no-op on a store with stable ids (Drive), where the id before and after
 * a move are the same string.
 */
async function rememberNewId(db: BookkeepingDB, oldId: RemoteId, newId: RemoteId): Promise<void> {
  if (oldId === newId) return;
  await db.transaction('rw', db.attachments, db.inboxItems, async () => {
    await db.attachments.where('driveFileId').equals(oldId).modify({ driveFileId: newId });
    await db.inboxItems.where('driveFileId').equals(oldId).modify({ driveFileId: newId });
  });
}

/** `stat` reports a gone file as undefined; filing one is a `not_found` failure. */
async function statOrThrow(id: RemoteId): Promise<RemoteEntry> {
  const store = getRemoteStore();
  const entry = await store.stat(id);
  if (!entry) throw new RemoteStoreError({ status: 404, provider: store.label, technical: `file ${id} is gone` });
  return entry;
}

/**
 * Rename and move a booked receipt from Inkorg/ to Kvitton/<år>/ and stamp
 * the verifikation on it. Idempotent: filing an already filed file again just
 * re-applies name, folder and properties.
 */
export async function fileReceipt(input: FileReceiptInput, db: BookkeepingDB = defaultDb): Promise<RemoteEntry> {
  const store = getRemoteStore();
  const company = await companyOrThrow(input.companyId, db);
  const folders = await ensureCompanyTree(company, db);
  const meta = await statOrThrow(input.driveFileId);
  const yearFolder = await ensureYearFolder(folders.kvitton, yearOf(input.verifikation.date));
  const name = receiptFileName({
    series: input.verifikation.series,
    number: input.verifikation.number,
    date: input.verifikation.date,
    counterparty: input.counterparty,
    amountOre: input.amountOre,
    extension: extensionFor(meta.name, meta.mediaType),
    ...(input.index !== undefined ? { index: input.index } : {}),
  });
  const from = meta.parents?.[0] ?? folders.inbox;
  const moved = await store.move(input.driveFileId, from, yearFolder, name);
  await rememberNewId(db, input.driveFileId, moved.id);
  await store.setMetadata(moved.id, receiptAppProperties(input.companyId, input.verifikation));
  return moved;
}

export interface FileRejectedInput {
  companyId: Uuid;
  driveFileId: string;
  /** Date deciding the year folder (receipt date); today when unknown. */
  date?: string | null;
  reason: 'private' | 'skipped';
}

/** Move a private/skipped file to Ej bokfört/<år>/. Nothing is ever deleted. */
export async function fileRejected(input: FileRejectedInput, db: BookkeepingDB = defaultDb): Promise<RemoteEntry> {
  const store = getRemoteStore();
  const company = await companyOrThrow(input.companyId, db);
  const folders = await ensureCompanyTree(company, db);
  const meta = await statOrThrow(input.driveFileId);
  const yearFolder = await ensureYearFolder(folders.ejBokfort, yearOf(input.date));
  const from = meta.parents?.[0] ?? folders.inbox;
  const moved = await store.move(input.driveFileId, from, yearFolder);
  await rememberNewId(db, input.driveFileId, moved.id);
  await store.setMetadata(moved.id, { companyId: input.companyId, status: input.reason });
  return moved;
}

export interface UploadReceiptCopyInput {
  companyId: Uuid;
  verifikation: Verifikation;
  counterparty: string;
  amountOre: Ore;
  blob: Blob;
  mediaType: string;
  index?: number;
}

/** Upload a receipt straight into Kvitton/<år>/ from a local blob (archive repair, offline catch-up). */
export async function uploadReceiptCopy(input: UploadReceiptCopyInput, db: BookkeepingDB = defaultDb): Promise<RemoteEntry> {
  const company = await companyOrThrow(input.companyId, db);
  const folders = await ensureCompanyTree(company, db);
  const yearFolder = await ensureYearFolder(folders.kvitton, yearOf(input.verifikation.date));
  const name = receiptFileName({
    series: input.verifikation.series,
    number: input.verifikation.number,
    date: input.verifikation.date,
    counterparty: input.counterparty,
    amountOre: input.amountOre,
    extension: extensionFor(undefined, input.mediaType),
    ...(input.index !== undefined ? { index: input.index } : {}),
  });
  return getRemoteStore().put({
    name,
    parentId: yearFolder,
    blob: input.blob,
    mediaType: input.mediaType,
    metadata: receiptAppProperties(input.companyId, input.verifikation),
  });
}
